/**
 * Fader Component implemented with Material UI
 * 
 * This component provides a slider control for adjusting continuous or discrete values
 * with Redux integration.
 */
import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Slider, SliderProps } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ReducersIndex from 'reduxModules/ducks';
import { getCurrentValue2 as getContainerValue } from 'reduxModules/selectors/containerSelectors';
import { isArray } from 'lodash';
import InputLabel from './InputLabel';
import { FaderProps } from 'types/components/atoms';
import { RootState } from 'types/redux/state';
import { GetCurrentValue2 } from 'types/redux/selectors';
import { UnknownAction } from '@reduxjs/toolkit';

// Type assertion for the imported getCurrentValue2 function
const getCurrentValue2 = getContainerValue as GetCurrentValue2;

// Interface for value and range from Redux
interface FaderValue {
  value: number | string;
  [key: string]: any; // Allow for dynamic range property
}

/**
 * Fader component
 * 
 * @param props - Component props
 * @returns A slider control component
 */
const Fader: React.FC<FaderProps> = (props) => {
  // Get current value and range from Redux state
  const stateValue = useSelector((state: RootState) => {
    // Ensure proper parameters are passed to getCurrentValue2 function
    return getCurrentValue2(state, props.dispatcher, props.name);
  }) as FaderValue | null;
  
  const currentValue = stateValue?.coverageDensity?.value;
  const range = stateValue?.coverageDensity ? stateValue.coverageDensity[props.range] : null;

  const discrete = isArray(range);
  const faderProps = discrete
    ? {
        min: 0,
        max: (range?.length || 1) - 1,
        step: 0.01,
        marks: Array.from({ length: range?.length || 1 }, (_, i) => ({ value: i })),
        valueLabelDisplay: 'off' as const
      }
    : range;

  // Calculate the correct value from Redux state
  const calculateValue = () => {
    if (currentValue !== undefined && range) {
      const calculatedValue = discrete ? range.indexOf(currentValue) : currentValue as number;
      // Ensure value is within bounds and valid
      return isNaN(calculatedValue) || calculatedValue < 0 ? 0 : calculatedValue;
    }
    return 0;
  };

  const [value, setValue] = useState<number>(() => calculateValue());
  const isUserInteracting = useRef(false);
  const hasUserSetValue = useRef(false);
  const dragDirection = useRef<'left' | 'right' | null>(null);
  const lastDragValue = useRef<number>(0);
  // Tracks the discrete range's own content (not just its array reference) so we can tell
  // "the user dragged the slider" apart from "the underlying geometry changed and produced a
  // different set of achievable values" — see the effect below.
  const prevRangeKey = useRef<string | null>(discrete ? JSON.stringify(range) : null);

  useEffect(() => {
    // For discrete sliders: only preserve the user's manual pick while the achievable-value
    // set (jumpPoints) is unchanged. A room/table/coverage change recomputes jumpPoints — when
    // that happens the old index no longer means the same thing (e.g. the last index used to
    // mean 12 mics, now it means a different count), so it must resync from Redux like a fresh
    // load would, rather than silently keeping a now-meaningless slider position.
    // For continuous sliders: Always sync.
    const newValue = calculateValue();
    const rangeKey = discrete ? JSON.stringify(range) : null;
    const rangeChanged = discrete && rangeKey !== prevRangeKey.current;
    prevRangeKey.current = rangeKey;

    if (!discrete || !hasUserSetValue.current || rangeChanged) {
      // Sync for continuous sliders, discrete sliders before user interaction, or whenever the
      // discrete range itself changed underneath the user's prior pick.
      if (rangeChanged) hasUserSetValue.current = false;
      setValue(newValue);
    }
    // For discrete sliders after user interaction, with an unchanged range: slider stays where
    // user left it.
  }, [discrete, currentValue, range]);

  const dispatch = useDispatch();

  // Update local state immediately for responsive UI
  const handleChange = (_: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      if (!isUserInteracting.current) {
        lastDragValue.current = newValue;
      } else if (newValue !== lastDragValue.current) {
        dragDirection.current = newValue > lastDragValue.current ? 'right' : 'left';
        lastDragValue.current = newValue;
      }
      isUserInteracting.current = true;
      hasUserSetValue.current = true;
      setValue(newValue);
    }
  };

  // Dispatch to Redux only when user releases the slider
  const handleChangeCommitted = (_: Event | React.SyntheticEvent, newValue: number | number[]) => {
    if (typeof newValue === 'number' && props.dispatcher) {
      // Snap in the direction of travel for discrete ranges
      const max = (range?.length || 1) - 1;
      const roundedValue = discrete
        ? Math.max(0, Math.min(max,
            dragDirection.current === 'right' ? Math.ceil(newValue) :
            dragDirection.current === 'left'  ? Math.floor(newValue) :
            Math.round(newValue)
          ))
        : newValue;

      // Snap the thumb visually to the stop point on release
      if (discrete) {
        setValue(roundedValue);
      }

      dragDirection.current = null;

      const dispatchValue = {
        [props.name]: {
          value: discrete && range ? range[roundedValue] : roundedValue
        }
      };

      if (props.dispatcher in ReducersIndex.container) {
        dispatch(ReducersIndex.container[props.dispatcher](dispatchValue) as UnknownAction);
      }

      // Clear interaction flag - slider stays at release position
      isUserInteracting.current = false;
    }
  };

  return (
    <Container className={props.className}>
      {props.label && <InputLabel>{props.label}</InputLabel>}
      <SliderContainer>
        <StyledSlider
          value={value}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          {...faderProps as SliderProps}
          sx={{ width: '100%' }}
        />
      </SliderContainer>
    </Container>
  );
};

// Styled components using MUI styled API
const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  width: '100%',
});

const SliderContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '2px',
});

const StyledSlider = styled(Slider)(({ theme }) => ({
  height: 8,
  '& .MuiSlider-thumb': {
    width: 18,
    height: 18,
    backgroundColor: '#1863D3',
    boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.05)',
    transition: 'none',
    '&:hover, &.Mui-focusVisible, &.Mui-active': {
      boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.05)',
    },
  },
  '& .MuiSlider-track': {
    height: 8,
    backgroundColor: '#1863D3',
    border: 'none',
  },
  '& .MuiSlider-rail': {
    height: 8,
    backgroundColor: '#E0E0E0',
    opacity: 1,
  },
  '& .MuiSlider-mark': {
    width: 2,
    height: 12,
    borderRadius: 2,
    backgroundColor: theme.palette.text.disabled,
    opacity: 1,
    transform: 'translateX(-50%) translateY(-50%)',
  },
  '& .MuiSlider-markActive': {
    backgroundColor: theme.palette.text.disabled,
    opacity: 1,
  },
}));


export type { FaderProps };
export default Fader;
